// Apolo IDE — AgentModal (Epic 18).
//
// Native <dialog> for creating / editing an agent. Used from the Chat tab
// sidebar. Submits to POST /api/agents or PUT /api/agents/:id.

import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Calendar, Check, Clock, Copy, Download, Globe, Inbox, Info, Link2, Loader2, Lock, NotebookPen, Plus, RefreshCw, Repeat, X } from 'lucide-react';
// Huge Icons — substituem os emojis decorativos da aba Chat (📡🔒📁 → ícones de
// traço, consistentes com o resto da UI). Emoji de agente (a.emoji) segue sendo
// dado do usuário via picker — não é tocado aqui.
import { HugeiconsIcon } from '@hugeicons/react';
import { RssIcon, LockIcon, FolderIcon } from '@hugeicons/core-free-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../../lib/api.js';
import { useLLMAdapters } from '../../hooks/useLLMAdapters.js';
import { useOllamaModels } from '../../hooks/useOllamaModels.js';
import { useUnifiedModels, adapterConnected } from '../../hooks/useUnifiedModels.js';
import AgentModelSelect from './AgentModelSelect.jsx';
import {
  isOllamaAdapter, computeModelSuggestions, computeDropdownModels, computeModelMismatch,
  autoCorrectModelOnAdapterChange, serializeSourcesIntoPrompt, buildAgentPayload, buildSchedulePayload,
} from './agentModalForm.js';
import MarkdownToolbar from '../shared/MarkdownToolbar.jsx';
import ModelStatusDot from '../shared/ModelStatusDot.jsx';
import { classifySource } from '../../hooks/useUrlPrefetch.js';
import { suggestRss } from '../../lib/rssDetection.js';

// ── Schedule helpers (inline — evita dep circular com SchedulesView) ──
const FREQ_OPTIONS = [
  { value: 'daily',    label: 'Todo dia',      runsPerMonth: 30 },
  { value: 'weekdays', label: 'Dias úteis',     runsPerMonth: 22 },
  { value: 'weekly',   label: 'Semanal (seg)',  runsPerMonth: 4  },
  { value: 'monthly',  label: 'Mensal (dia 1)', runsPerMonth: 1  },
  { value: 'every_6h', label: 'A cada 6h',      runsPerMonth: 120},
];
// buildCron/buildCronDatetime moved to ./agentModalForm.js (used by buildSchedulePayload).

/**
 * Reverse of buildCron + buildCronDatetime — parses a cron string back into
 * the sched state shape. Conservative: unknown patterns fall back to daily 09:00.
 *
 * @param {string} expr
 * @returns {{ mode:'recurring'|'once', frequency:string, time:string, oneTime:string }}
 */
function parseCron(expr) {
  const fallback = { mode: 'recurring', frequency: 'daily', time: '09:00', oneTime: SCHEDULE_DEFAULTS.oneTime };
  if (typeof expr !== 'string') return fallback;
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return fallback;
  const [m, h, dom, mon, dow] = parts;
  const pad = (n) => String(n).padStart(2, '0');
  // every_6h: "M */6 * * *"
  if (h === '*/6' && dom === '*' && mon === '*' && dow === '*') {
    return { ...fallback, frequency: 'every_6h', time: `00:${pad(m)}` };
  }
  // Concrete hour/minute below
  const time = `${pad(h)}:${pad(m)}`;
  // weekdays: "M H * * 1-5"
  if (dom === '*' && mon === '*' && dow === '1-5') return { ...fallback, frequency: 'weekdays', time };
  // weekly: "M H * * 1"
  if (dom === '*' && mon === '*' && dow === '1') return { ...fallback, frequency: 'weekly', time };
  // monthly: "M H 1 * *"
  if (dom === '1' && mon === '*' && dow === '*') return { ...fallback, frequency: 'monthly', time };
  // once: "M H D Mon *" — concrete day-of-month + concrete month
  const domNum = Number(dom);
  const monNum = Number(mon);
  if (Number.isInteger(domNum) && Number.isInteger(monNum) && dow === '*') {
    const year = new Date().getFullYear();
    const oneTime = `${year}-${pad(monNum)}-${pad(domNum)}T${time}`;
    return { mode: 'once', frequency: 'daily', time, oneTime };
  }
  // daily: "M H * * *"
  if (dom === '*' && mon === '*' && dow === '*') return { ...fallback, frequency: 'daily', time };
  return fallback;
}
const SCHEDULE_DEFAULTS = {
  enabled: false,
  mode: 'recurring',
  frequency: 'daily',
  time: '09:00',
  oneTime: (() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T09:00`;
  })(),
  outputDest: 'vault_obsidian',
};

// Modelos recomendados pra começar — ordenados por uso. Cards "Recomendado"
// no topo, "Mais leve" pra hardware pequeno. Qwen 2.5 Coder em destaque
// pra dev workflows (Cowork tab). Pedro pediu a família Qwen completa.
const OLLAMA_QUICK_START = [
  {
    name: 'qwen2.5-coder:14b',
    sizeGb: 9,
    ram: '16GB+',
    badge: 'Top código',
    desc: 'Top tier code — refactor, debug, multi-file',
  },
  {
    name: 'qwen2.5:14b',
    sizeGb: 8.4,
    ram: '16GB+',
    badge: 'Mais robusto',
    desc: 'Generalista forte · PT-BR · 128k context',
  },
  {
    name: 'qwen2.5-coder:7b',
    sizeGb: 4.7,
    ram: '8GB+',
    badge: 'Code leve',
    desc: 'Code decente, baixa RAM',
  },
  {
    name: 'qwen2.5:7b',
    sizeGb: 4.4,
    ram: '8GB+',
    badge: 'Recomendado',
    desc: 'Equilibrado · PT-BR excelente · tools',
  },
  {
    name: 'qwen3:8b',
    sizeGb: 4.9,
    ram: '12GB+',
    badge: 'Mais novo',
    desc: 'Última geração Qwen · melhor reasoning',
  },
  {
    name: 'llama3.2:3b',
    sizeGb: 2,
    ram: '4GB+',
    badge: 'Mais leve',
    desc: 'Mais rápido · qualidade básica',
  },
];

const DEFAULTS = {
  name: '',
  emoji: '🤖',
  system_prompt: '',
  model: 'anthropic/claude-opus-4-7-medium',
  temperature: 0.7,
  max_tokens: 4096,
  llm_adapter_id: null,
};

// OLLAMA_COMMON_MODELS moved to ./agentModalForm.js (used by computeModelSuggestions).

export default function AgentModal({ open, agent, onClose, onSaved }) {
  const ref = useRef(null);
  const systemPromptRef = useRef(null);
  const [form, setForm] = useState({ ...DEFAULTS });
  const [sched, setSched] = useState({ ...SCHEDULE_DEFAULTS });
  // Linked scheduled_task id when an existing agent already has one. Lets the
  // save flow PATCH/DELETE instead of duplicating on every save.
  const [scheduleTaskId, setScheduleTaskId] = useState(null);
  // Fontes de conhecimento — URLs que Apolo pre-fetcha antes de cada chamada
  // do agente. Cada item: { url, status: 'unchecked'|'ok'|'auth'|'error', error? }
  const [sources, setSources] = useState([]);
  const [newSource, setNewSource] = useState('');
  const [testingIdx, setTestingIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Sprint v5 A3: filter to text adapters only (chat dispatch). Hook auto-
  // fetches once on mount; we read inside render so closed modal still pays
  // for it but the data is cached behind apiFetch.
  const { adapters, listModels } = useLLMAdapters({ kind: 'text' });
  const textAdapters = (adapters || []).filter((a) => a.is_active);

  // Adapter selecionado — fonte de verdade pra modelos disponíveis.
  const selectedAdapter = useMemo(
    () => textAdapters.find((a) => a.id === form.llm_adapter_id) || null,
    [textAdapters, form.llm_adapter_id],
  );

  // Dropdown de modelo (pedido 2026-07-10): sonda LIVE os modelos do adapter
  // selecionado (POST /adapters/:id/models) e cacheia por adapter — a lista do
  // dropdown reflete o que o adapter REALMENTE serve, não só o registry.
  // Falha/vazio ⇒ {} e o dropdown cai no fallback (available_models → registry).
  const [liveModelsByAdapter, setLiveModelsByAdapter] = useState({});
  useEffect(() => {
    const id = selectedAdapter?.id;
    if (!id || liveModelsByAdapter[id] !== undefined) return;
    let stale = false;
    (async () => {
      const res = await listModels(id).catch(() => null);
      const models = res?.data?.ok && Array.isArray(res.data.models) ? res.data.models : [];
      if (!stale) setLiveModelsByAdapter((m) => ({ ...m, [id]: models }));
    })();
    return () => { stale = true; };
  }, [selectedAdapter?.id, listModels, liveModelsByAdapter]);

  // Status de conexão unificado (bolinha verde). Fonte única: useUnifiedModels.
  // Quando há adapter explícito, reflete o adapter; senão (Padrão / static
  // registry) cai pro status do próprio modelo resolvido por id.
  const { byId: unifiedById } = useUnifiedModels({ kind: 'text' });
  const modelConnected = useMemo(() => {
    if (selectedAdapter) return adapterConnected(selectedAdapter);
    return unifiedById[form.model]?.connected === true;
  }, [selectedAdapter, unifiedById, form.model]);

  // Detecta se é Ollama (case-insensitive no slug ou display_name).
  const isOllama = useMemo(() => isOllamaAdapter(selectedAdapter), [selectedAdapter]);

  // Busca modelos locais REAIS via IPC só quando Ollama tá selecionado
  // (e modal tá aberto pra evitar chamadas em background).
  const {
    models: ollamaLocalModels,
    loading: ollamaLoading,
    offline: ollamaOffline,
    refresh: refreshOllama,
  } = useOllamaModels({
    baseUrl: selectedAdapter?.base_url || 'http://127.0.0.1:11434',
    enabled: open && isOllama,
  });

  // Datalist suggestions + dropdown options + mismatch — lógica pura no agentModalForm
  // (prioridade Ollama-local→available_models→registry-por-provider→registry; prefixo de provider).
  const modelSuggestions = useMemo(
    () => computeModelSuggestions({ selectedAdapter, isOllama, ollamaLocalModels }),
    [selectedAdapter, isOllama, ollamaLocalModels],
  );
  const dropdownModels = useMemo(
    () => computeDropdownModels({ selectedAdapter, liveModels: liveModelsByAdapter[selectedAdapter?.id], modelSuggestions }),
    [selectedAdapter, liveModelsByAdapter, modelSuggestions],
  );
  const modelMismatch = useMemo(
    () => computeModelMismatch({ selectedAdapter, isOllama, model: form.model }),
    [selectedAdapter, isOllama, form.model],
  );

  // Trocar adapter — auto-correção do modelo em mismatch óbvio (agentModalForm).
  function handleAdapterChange(adapterId) {
    const next = textAdapters.find((a) => a.id === adapterId);
    setForm((prev) => autoCorrectModelOnAdapterChange(prev, next));
  }

  useEffect(() => {
    if (open && ref.current && !ref.current.open) ref.current.showModal();
    if (!open && ref.current && ref.current.open) ref.current.close();
  }, [open]);

  useEffect(() => {
    setForm(agent ? { ...DEFAULTS, ...agent } : { ...DEFAULTS });
    setSched({ ...SCHEDULE_DEFAULTS });
    setScheduleTaskId(null);
    // Sources serialiados em system_prompt entre marcadores. Extrai ao abrir.
    const prompt = agent?.system_prompt || '';
    const match = prompt.match(/<!-- apolo:sources -->([\s\S]*?)<!-- \/apolo:sources -->/);
    if (match) {
      const urls = match[1].trim().split('\n').map((l) => l.trim()).filter(Boolean);
      setSources(urls.map((url) => ({ url, status: 'unchecked' })));
    } else {
      setSources([]);
    }
    setNewSource('');
  }, [agent]);

  // Sprint v11+ fix — hidrata o toggle "Executar automaticamente" a partir
  // do scheduled_task linkado ao agent_id. Sem isso, o toggle sempre voltava
  // pra OFF quando o modal reabria, mesmo com schedule salvo.
  useEffect(() => {
    if (!open || !agent?.id) return;
    let cancelled = false;
    (async () => {
      const res = await apiFetch('/api/code-ide/schedule');
      if (cancelled || !res.ok) return;
      const tasks = res.data?.tasks || res.data?.items || res.data || [];
      // Prefer match por agent_id (column existe). Fallback por nome legado
      // pra agents que foram criados antes do agent_id ser persisted.
      const match = tasks.find((t) => t.agent_id === agent.id)
        || tasks.find((t) => t.name === `${agent.name} — auto`);
      if (!match) return;
      const parsed = parseCron(match.cron_expression);
      let outputDest = SCHEDULE_DEFAULTS.outputDest;
      try {
        // Defensive parse: tasks salvos antes do fix v10.1 estão double-
        // encoded (JSON.stringify aplicado 2x). Se o primeiro parse retornar
        // string em vez de objeto, parseia de novo.
        let payload = typeof match.command_payload === 'string'
          ? JSON.parse(match.command_payload)
          : (match.command_payload || {});
        if (typeof payload === 'string') {
          payload = JSON.parse(payload);
        }
        if (typeof payload.output_destination === 'string') {
          outputDest = payload.output_destination;
        }
      } catch { /* ignore */ }
      setSched({ enabled: match.status !== 'paused' && !match.deleted_at, ...parsed, outputDest });
      setScheduleTaskId(match.id);
    })();
    return () => { cancelled = true; };
  }, [open, agent?.id, agent?.name]);

  // Detecta auth wall comum (Google, Notion, Slack, etc.) só pelo hostname
  // antes mesmo de testar — feedback instantâneo enquanto digita.
  function detectAuthWall(url) {
    if (classifySource(url) === 'file') return null; // arquivos locais não têm auth wall
    const lower = url.toLowerCase();
    const authHosts = [
      { match: 'notebooklm.google.com', label: 'NotebookLM (Google login)' },
      { match: 'mail.google.com',       label: 'Gmail (Google login)' },
      { match: 'drive.google.com',      label: 'Google Drive (login)' },
      { match: 'docs.google.com',       label: 'Google Docs (login)' },
      { match: 'notion.so',             label: 'Notion (login)' },
      { match: 'app.slack.com',         label: 'Slack (login)' },
      { match: 'linkedin.com/in/',      label: 'LinkedIn (login)' },
    ];
    for (const a of authHosts) if (lower.includes(a.match)) return a.label;
    return null;
  }

  async function testSource(idx) {
    setTestingIdx(idx);
    const src = sources[idx];
    const kind = classifySource(src.url);
    if (!kind || !window.apolo?.fetch) {
      setSources((prev) => prev.map((s, i) => i === idx ? { ...s, status: 'error', error: !kind ? 'Formato não reconhecido' : 'Apolo fetch indisponível' } : s));
      setTestingIdx(null);
      return;
    }
    let res;
    if (kind === 'file') {
      if (!window.apolo.fetch.file) {
        res = { ok: false, error: 'apolo.fetch.file indisponível neste build' };
      } else {
        res = await window.apolo.fetch.file({ path: src.url, maxChars: 200 });
      }
    } else {
      res = await window.apolo.fetch.url({ url: src.url, maxChars: 200 });
    }
    setSources((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      if (res.ok) return { ...s, kind, status: 'ok', chars: res.chars };
      if (res.error?.toLowerCase().includes('autentic')) return { ...s, kind, status: 'auth', error: res.error };
      return { ...s, kind, status: 'error', error: res.error || 'Falha' };
    }));
    setTestingIdx(null);
  }

  function addSource() {
    const raw = newSource.trim();
    if (!raw) return;
    const kind = classifySource(raw);
    if (!kind) {
      setError('Entrada inválida — use URL http(s) ou path local (/foo/bar.md, ~/notes.txt, file://...)');
      return;
    }
    if (kind === 'url') {
      try { new URL(raw); } catch { setError('URL inválida'); return; }
    }
    setError(null);
    setSources((prev) => [...prev, { url: raw, kind, status: 'unchecked' }]);
    setNewSource('');
  }

  // Aplica sugestão RSS num índice — se confidence='known', troca em silêncio;
  // se 'guess', mantém a original e adiciona a guess pro user testar.
  function applyRssSuggestion(idx) {
    setSources((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const sugg = suggestRss(s.url);
      if (!sugg.suggested) return s;
      return { ...s, url: sugg.suggested, status: 'unchecked', chars: undefined, error: undefined };
    }));
  }

  // Converte TODAS as URLs http(s) pra RSS (lookup-based). Paths locais
  // ficam intactos. Items já com RSS detectado também.
  function convertAllToRss() {
    setSources((prev) => prev.map((s) => {
      if (classifySource(s.url) !== 'url') return s;
      const sugg = suggestRss(s.url);
      if (!sugg.suggested || sugg.confidence !== 'known') return s;
      return { ...s, url: sugg.suggested, status: 'unchecked', chars: undefined, error: undefined };
    }));
  }

  function removeSource(idx) {
    setSources((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError('Nome obrigatório');
      return;
    }
    setSubmitting(true);
    const url = agent?.id ? `/api/agents/${agent.id}` : '/api/agents';
    const method = agent?.id ? 'PUT' : 'POST';

    // Serializa sources no system_prompt entre marcadores HTML (agentModalForm) — assim
    // sobrevive ao schema atual sem migration; ChatView extrai e pre-fetcha as URLs.
    const systemPrompt = serializeSourcesIntoPrompt(form.system_prompt, sources);
    const payload = buildAgentPayload(form, systemPrompt);
    const res = await apiFetch(url, { method, body: payload });
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || 'falha');
      return;
    }
    const savedAgent = res.data.agent;

    // Schedule lifecycle — POST quando enabled+sem task; PATCH quando
    // enabled+task existente; DELETE quando disabled+task existente.
    // Liga via agent_id pra evitar duplicação por rename.
    const linkedAgentId = savedAgent?.id || agent?.id || null;
    if (sched.enabled && form.system_prompt?.trim()) {
      // cron + command payload (agentModalForm). O service stringifica o payload
      // internamente (objeto, NÃO string). execution_target='local' pra adapters LOCAIS
      // (Ollama OU LM Studio) — o Worker remoto não alcança o localhost do user; o cron
      // fica no fuso LOCAL (senão "11:00" virava 11:00 UTC).
      const { cron, payload } = buildSchedulePayload({ form, sched, isOllama, selectedAdapter });
      // Sem .catch silencioso — falhas precisam aparecer no banner de erro.
      let schedRes;
      if (scheduleTaskId) {
        schedRes = await apiFetch(`/api/code-ide/schedule/${scheduleTaskId}`, {
          method: 'PATCH',
          body: {
            name: `${form.name.trim()} — auto`,
            cron_expression: cron,
            command_payload: payload,
            llm_adapter_id: form.llm_adapter_id || null,
            agent_id: linkedAgentId,
            status: 'enabled',
          },
        });
      } else {
        schedRes = await apiFetch('/api/code-ide/schedule', {
          method: 'POST',
          body: {
            name: `${form.name.trim()} — auto`,
            cron_expression: cron,
            command_type: 'prompt',
            llm_adapter_id: form.llm_adapter_id || null,
            agent_id: linkedAgentId,
            command_payload: payload,
          },
        });
      }
      if (!schedRes.ok) {
        setError(`Agente salvo, mas schedule falhou: ${schedRes.error || 'erro desconhecido'}`);
        return;
      }
    } else if (!sched.enabled && scheduleTaskId) {
      // Toggle desligado mas tinha task antes — apaga.
      const delRes = await apiFetch(`/api/code-ide/schedule/${scheduleTaskId}`, {
        method: 'DELETE',
      });
      if (!delRes.ok) {
        setError(`Agente salvo, mas remoção do schedule falhou: ${delRes.error || 'erro desconhecido'}`);
        return;
      }
    }

    onSaved?.(savedAgent);
    onClose?.();
  }

  return (
    <dialog
      ref={ref}
      aria-labelledby="agent-modal-title"
      onClose={onClose}
      className="m-0 h-[100dvh] max-h-none w-screen max-w-none rounded-none border-0 bg-apolo-surface1 p-0 text-apolo-textPrimary backdrop:bg-apolo-overlay lg:m-auto lg:h-auto lg:max-h-[90vh] lg:w-auto lg:max-w-fit lg:rounded-xl lg:border lg:border-apolo-borderSubtle"
    >
      <form
        onSubmit={handleSubmit}
        className="flex h-full w-full flex-col gap-3 overflow-y-auto p-5 lg:h-auto lg:w-[min(28rem,92vw)] lg:overflow-visible"
        style={{
          paddingTop: 'max(1.25rem, env(safe-area-inset-top))',
          paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))',
        }}
      >
        <header className="flex items-center justify-between">
          <h2 id="agent-modal-title" className="text-lg font-medium">
            {agent?.id ? 'Editar agente' : 'Novo agente'}
          </h2>
          <button
            type="button"
            aria-label="Fechar"
            onClick={onClose}
            className="inline-flex h-11 w-11 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:h-8 lg:w-8"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={form.emoji}
            onChange={(e) => setForm({ ...form, emoji: e.target.value })}
            maxLength={4}
            aria-label="Emoji"
            className="w-12 rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-center text-base"
          />
          <input
            type="text"
            required
            placeholder="Nome do agente"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="flex-1 rounded-md border border-apolo-border bg-apolo-surface2 px-3 py-2 text-sm focus:border-apolo-accent focus:outline-none"
          />
        </div>

        <div className="flex flex-col gap-1 text-sm">
          <span className="text-apolo-textSecondary">System prompt</span>
          <div className="overflow-hidden rounded-md border border-apolo-border bg-apolo-surface2 focus-within:border-apolo-accent focus-within:ring-2 focus-within:ring-apolo-accentRing">
            <div className="border-b border-apolo-borderSubtle/40 bg-apolo-surface2">
              <MarkdownToolbar textareaRef={systemPromptRef} value={form.system_prompt} onChange={(v) => setForm({ ...form, system_prompt: v })} compact />
            </div>
            <textarea
              ref={systemPromptRef}
              rows={5}
              value={(form.system_prompt || '').replace(/\n*<!-- apolo:sources -->[\s\S]*?<!-- \/apolo:sources -->\n*/g, '')}
              onChange={(e) => setForm({ ...form, system_prompt: e.target.value })}
              placeholder="Você é um assistente útil que..."
              className="block w-full resize-y border-0 bg-transparent px-3 py-2 font-mono text-xs focus:outline-none"
            />
          </div>
        </div>

        {/* ── Fontes de conhecimento (URLs + arquivos locais) ─────────── */}
        <div className="flex flex-col gap-2 rounded-xl border border-apolo-borderSubtle bg-apolo-surface2/40 p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Link2 size={13} aria-hidden="true" className="text-apolo-accent" />
              <span className="text-xs font-medium text-apolo-textPrimary">Fontes de conhecimento</span>
              {sources.length > 0 && (
                <span className="rounded-full bg-apolo-accent/15 px-1.5 text-[9px] font-medium text-apolo-accent">{sources.length}</span>
              )}
            </div>
            {/* Botão "Converter pra RSS" — só aparece se há URLs com RSS conhecido. */}
            {(() => {
              const convertible = sources.filter((s) => {
                if (classifySource(s.url) !== 'url') return false;
                const r = suggestRss(s.url);
                return r.suggested && r.confidence === 'known';
              });
              if (convertible.length === 0) {
                return <span className="text-[10px] text-apolo-textTertiary">Lido antes de cada mensagem</span>;
              }
              return (
                <button
                  type="button"
                  onClick={convertAllToRss}
                  title={`Converte ${convertible.length} URL(s) pra feeds RSS — gasta menos tokens`}
                  className="inline-flex items-center gap-1 rounded-md border border-apolo-accent/40 bg-apolo-accent/10 px-2 py-0.5 text-[10px] font-medium text-apolo-accent transition hover:bg-apolo-accent/20"
                >
                  <HugeiconsIcon icon={RssIcon} size={11} aria-hidden="true" />
                  Converter {convertible.length} pra RSS
                </button>
              );
            })()}
          </div>

          {/* Lista de fontes */}
          {sources.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {sources.map((s, i) => {
                const kind = s.kind || classifySource(s.url) || 'url';
                const authLabel = detectAuthWall(s.url);
                const isAuth = s.status === 'auth' || authLabel;
                const isOk = s.status === 'ok';
                const isErr = s.status === 'error';
                const testing = testingIdx === i;
                const KindIcon = kind === 'file' ? NotebookPen : Globe;
                let statusColor = 'text-apolo-textTertiary';
                let StatusIcon = KindIcon;
                let tooltip = kind === 'file' ? 'Arquivo local · não testado' : 'URL · não testada';
                if (testing)       { StatusIcon = Loader2; statusColor = 'text-apolo-accent'; tooltip = 'Lendo...'; }
                else if (isOk)     { StatusIcon = Check; statusColor = 'text-apolo-success'; tooltip = `OK · ${s.chars || 0} chars`; }
                else if (isAuth)   { StatusIcon = Lock; statusColor = 'text-apolo-warning'; tooltip = authLabel || s.error || 'Requer login'; }
                else if (isErr)    { StatusIcon = AlertTriangle; statusColor = 'text-apolo-error'; tooltip = s.error || 'Erro'; }

                // Sugestão de RSS específica desta URL
                const sugg = kind === 'url' ? suggestRss(s.url) : { suggested: null };

                return (
                  <li
                    key={`${s.url}-${i}`}
                    className="flex flex-col gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface3 px-2 py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <StatusIcon
                        size={11}
                        aria-hidden="true"
                        className={`shrink-0 ${statusColor} ${testing ? 'animate-spin' : ''}`}
                        data-tip={tooltip}
                      />
                      <code className="flex-1 truncate font-mono text-[11px] text-apolo-textPrimary" title={s.url}>
                        {s.url}
                      </code>
                      {kind === 'file' && (
                        <span className="shrink-0 rounded bg-apolo-accent/10 px-1 text-[9px] font-medium text-apolo-accent" title="Arquivo local">
                          arquivo
                        </span>
                      )}
                      {authLabel && (
                        <span className="inline-flex items-center gap-0.5 shrink-0 text-[9px] text-apolo-warning" title={authLabel}>
                          <HugeiconsIcon icon={LockIcon} size={9} aria-hidden="true" />
                          auth
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => testSource(i)}
                        disabled={testing}
                        aria-label="Testar"
                        title="Testar acessibilidade"
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary disabled:opacity-40"
                      >
                        <RefreshCw size={10} aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeSource(i)}
                        aria-label="Remover"
                        title="Remover"
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-error"
                      >
                        <X size={10} aria-hidden="true" />
                      </button>
                    </div>
                    {sugg.suggested && sugg.confidence === 'known' && (
                      <button
                        type="button"
                        onClick={() => applyRssSuggestion(i)}
                        className="ml-5 inline-flex items-center gap-1 self-start text-[10px] text-apolo-accent transition hover:underline"
                        title={sugg.reason}
                      >
                        <HugeiconsIcon icon={RssIcon} size={10} aria-hidden="true" />
                        Usar RSS: <code className="font-mono">{sugg.suggested}</code>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* Input pra adicionar URL ou arquivo local */}
          {(() => {
            const trimmed = newSource.trim();
            const liveKind = classifySource(trimmed);
            const liveSugg = liveKind === 'url' ? suggestRss(trimmed) : null;
            return (
              <>
                <div className="flex gap-1.5">
                  <input
                    type="text"
                    value={newSource}
                    onChange={(e) => setNewSource(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        addSource();
                      }
                    }}
                    placeholder="https://site.com/rss · /Users/pedro/notes.md · ~/Documents/foo.txt"
                    className="h-7 flex-1 rounded-md border border-apolo-border bg-apolo-surface3 px-2 font-mono text-[11px] text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={addSource}
                    disabled={!trimmed}
                    className="inline-flex h-7 items-center gap-1 rounded-md bg-apolo-accent px-2.5 text-[11px] font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-50"
                  >
                    <Plus size={10} aria-hidden="true" />
                    Adicionar
                  </button>
                </div>
                {/* Live preview da sugestão RSS antes de adicionar */}
                {liveSugg?.suggested && liveSugg.confidence === 'known' && (
                  <button
                    type="button"
                    onClick={() => { setNewSource(liveSugg.suggested); }}
                    className="inline-flex items-center gap-1 self-start text-[10px] text-apolo-accent transition hover:underline"
                    title={liveSugg.reason}
                  >
                    <HugeiconsIcon icon={RssIcon} size={10} aria-hidden="true" />
                    RSS detectado: <code className="font-mono">{liveSugg.suggested}</code> — clique pra trocar
                  </button>
                )}
                {trimmed && !liveKind && (
                  <span className="text-[10px] text-apolo-warning">
                    Formato não reconhecido. Use URL http(s) ou path absoluto (/foo.md, ~/notes.txt, file://...).
                  </span>
                )}
                {liveKind === 'file' && (
                  <span className="inline-flex items-start gap-1 text-[10px] text-apolo-textTertiary">
                    <HugeiconsIcon icon={FolderIcon} size={11} aria-hidden="true" className="mt-px shrink-0" />
                    Arquivo local — será lido a cada mensagem. Aceita .md, .txt, .json, .csv, .yaml, .xml.
                  </span>
                )}
              </>
            );
          })()}

          {/* Aviso quando há URL com auth */}
          {sources.some((s) => detectAuthWall(s.url) || s.status === 'auth') && (
            <div className="flex items-start gap-1.5 rounded-md border border-apolo-warning/30 bg-apolo-warning/5 p-2 text-[10px] leading-relaxed text-apolo-textSecondary">
              <Lock size={11} aria-hidden="true" className="mt-0.5 shrink-0 text-apolo-warning" />
              <div>
                <p className="font-medium text-apolo-warning">URL requer login</p>
                <p className="text-apolo-textTertiary">
                  NotebookLM, Gmail, Drive e similares precisam da sessão do seu browser. Apolo ainda não tem o bridge — apenas URLs públicas (TechCrunch, HN, RSS, etc.) funcionam direto. Workaround pro NotebookLM: copia o conteúdo do notebook e cola no system prompt.
                </p>
              </div>
            </div>
          )}
        </div>

        <label className="flex flex-col gap-1 text-xs">
          <span className="text-apolo-textSecondary">Adapter (opcional)</span>
          <select
            value={form.llm_adapter_id || ''}
            onChange={(e) => handleAdapterChange(e.target.value)}
            className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1 text-xs"
          >
            <option value="">Padrão (static registry)</option>
            {textAdapters.map((a) => (
              <option key={a.id} value={a.id}>
                {a.display_name}
                {a.has_api_key ? '' : ' (sem API key)'}
              </option>
            ))}
          </select>
          <span className="text-[10px] text-apolo-textTertiary">
            {isOllama
              ? 'Ollama local — sem API key necessária. Use modelos que você já pulou via `ollama pull <nome>`.'
              : 'Override do registry estático. Sem efeito quando sem API key.'}
          </span>
        </label>

        <div className="flex gap-2">
          <div className="flex flex-1 flex-col gap-1 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-apolo-textSecondary">
                Modelo
                <ModelStatusDot
                  connected={modelConnected}
                  title={modelConnected
                    ? (selectedAdapter ? `${selectedAdapter.display_name} conectado` : 'Modelo conectado')
                    : (selectedAdapter ? `${selectedAdapter.display_name} sem API key / inativo` : 'Sem API key / inativo')}
                />
              </span>
              {isOllama && (
                <button
                  type="button"
                  onClick={refreshOllama}
                  disabled={ollamaLoading}
                  className="inline-flex items-center gap-1 text-[10px] text-apolo-textTertiary transition hover:text-apolo-textPrimary disabled:opacity-50"
                  title="Recarregar lista do Ollama local"
                >
                  <RefreshCw
                    size={10}
                    aria-hidden="true"
                    className={ollamaLoading ? 'animate-spin' : ''}
                  />
                  {ollamaLoading ? 'buscando…' : 'atualizar'}
                </button>
              )}
            </div>

            {/* Ollama: picker visual estilo ollama.com com lista dos modelos
              * REAIS que o user tem pulado localmente. Cai pra input livre se
              * Ollama tá offline. */}
            {isOllama && !ollamaOffline && ollamaLocalModels.length > 0 ? (
              <OllamaModelPicker
                models={ollamaLocalModels}
                value={form.model}
                onChange={(name) => setForm({ ...form, model: name })}
              />
            ) : (
              /* Dropdown com as LLMs disponíveis no adapter (live probe →
               * available_models → registry). "Outro (digitar)…" = input livre. */
              <AgentModelSelect
                value={form.model}
                options={dropdownModels}
                onChange={(m) => setForm({ ...form, model: m })}
                mismatch={modelMismatch}
              />
            )}

            {/* Ollama status badge — feedback claro do estado da conexão */}
            {isOllama && !ollamaLoading && (
              <div className="mt-1 flex items-center gap-1.5 text-[10px]">
                {ollamaOffline ? (
                  <>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-apolo-error" />
                    <span className="text-apolo-error">Não detectado em {selectedAdapter?.base_url || '127.0.0.1:11434'}</span>
                  </>
                ) : ollamaLocalModels.length === 0 ? (
                  <>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-apolo-warning" />
                    <span className="text-apolo-warning">Ollama online — sem modelos pulados</span>
                  </>
                ) : (
                  <>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-apolo-success" />
                    <span className="text-apolo-success">
                      Conectado · {ollamaLocalModels.length} modelo{ollamaLocalModels.length !== 1 ? 's' : ''} disponível{ollamaLocalModels.length !== 1 ? 'is' : ''}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Empty state com quick-start cards pulláveis */}
            {isOllama && !ollamaOffline && ollamaLocalModels.length === 0 && !ollamaLoading && (
              <OllamaQuickStart
                onPullStarted={(modelName) => setForm((p) => ({ ...p, model: modelName }))}
                onRefresh={refreshOllama}
              />
            )}

            {isOllama && ollamaOffline && (
              <div className="mt-1 rounded-md border border-apolo-error/30 bg-apolo-error/5 p-2 text-[10px] leading-relaxed text-apolo-textSecondary">
                <p className="mb-1 font-medium text-apolo-error">Ollama não está rodando</p>
                <p className="text-apolo-textTertiary">
                  Abre o terminal e roda <code className="rounded bg-apolo-surface3 px-1 font-mono">ollama serve</code>, depois clica em <strong>atualizar</strong> acima.
                </p>
              </div>
            )}
            {modelMismatch && selectedAdapter && !isOllama && (
              <span className="inline-flex items-start gap-1 text-[10px] leading-tight text-apolo-warning">
                <Info size={10} aria-hidden="true" className="mt-0.5 shrink-0" />
                <span>
                  Esse modelo não bate com o adapter <strong>{selectedAdapter.display_name}</strong>.
                  {selectedAdapter.default_model ? ` Sugestão: ${selectedAdapter.default_model}` : ''}
                </span>
              </span>
            )}
          </div>
          <label className="flex w-24 flex-col gap-1 text-xs">
            <span className="text-apolo-textSecondary">Temp</span>
            <input
              type="number"
              step="0.1"
              min="0"
              max="2"
              value={form.temperature}
              onChange={(e) => setForm({ ...form, temperature: e.target.value })}
              className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1 text-xs"
            />
          </label>
          <label className="flex w-24 flex-col gap-1 text-xs">
            <span className="text-apolo-textSecondary">Max tk</span>
            <input
              type="number"
              min="1"
              value={form.max_tokens}
              onChange={(e) => setForm({ ...form, max_tokens: e.target.value })}
              className="rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1 text-xs"
            />
          </label>
        </div>

        {/* ── Seção Schedule ──────────────────────────────────────────── */}
        <div className="rounded-xl border border-apolo-borderSubtle bg-apolo-surface2/40 p-3">
          {/* Toggle row */}
          <button
            type="button"
            onClick={() => setSched((s) => ({ ...s, enabled: !s.enabled }))}
            className="flex w-full items-center justify-between gap-3 focus:outline-none"
            aria-expanded={sched.enabled}
          >
            <div className="flex items-center gap-2">
              <Calendar size={14} aria-hidden="true" className="text-apolo-accent" />
              <span className="text-xs font-medium text-apolo-textPrimary">
                Executar automaticamente
              </span>
              {sched.enabled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-apolo-accent/15 px-1.5 py-0 text-[9px] font-medium text-apolo-accent">
                  ativo
                </span>
              )}
            </div>
            {/* Toggle pill */}
            <span
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 transition-colors ${
                sched.enabled
                  ? 'border-apolo-accent bg-apolo-accent'
                  : 'border-apolo-borderStrong bg-apolo-surface3'
              }`}
              aria-hidden="true"
            >
              <span
                className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                  sched.enabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </span>
          </button>

          {/* Schedule config — animado */}
          <AnimatePresence initial={false}>
            {sched.enabled && (
              <motion.div
                key="sched-body"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="flex flex-col gap-3 pt-3">
                  {/* Recorrente / Uma vez */}
                  <div
                    role="tablist"
                    aria-label="Tipo de schedule"
                    className="inline-flex w-fit items-center gap-0.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface3 p-0.5"
                  >
                    {[
                      { value: 'recurring', label: 'Recorrente', Icon: Repeat },
                      { value: 'once',      label: 'Uma vez',    Icon: Clock  },
                    ].map(({ value, label, Icon }) => {
                      const active = sched.mode === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          onClick={() => setSched((s) => ({ ...s, mode: value }))}
                          className={`relative inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 text-[11px] font-medium transition-colors focus:outline-none ${
                            active ? 'text-apolo-textPrimary' : 'text-apolo-textTertiary hover:text-apolo-textSecondary'
                          }`}
                        >
                          {active && (
                            <motion.span
                              layoutId="agent-sched-tab"
                              transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                              aria-hidden="true"
                              className="absolute inset-0 rounded-full bg-apolo-surface1"
                            />
                          )}
                          <Icon size={10} aria-hidden="true" className="relative z-10" />
                          <span className="relative z-10">{label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {sched.mode === 'recurring' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">Frequência</span>
                        <select
                          value={sched.frequency}
                          onChange={(e) => setSched((s) => ({ ...s, frequency: e.target.value }))}
                          className="h-8 rounded-md border border-apolo-border bg-apolo-surface3 px-2 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
                        >
                          {FREQ_OPTIONS.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                      {sched.frequency !== 'every_6h' && (
                        <div className="flex flex-col gap-1">
                          <span className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">Horário</span>
                          <input
                            type="time"
                            value={sched.time}
                            onChange={(e) => setSched((s) => ({ ...s, time: e.target.value }))}
                            className="h-8 rounded-md border border-apolo-border bg-apolo-surface3 px-2 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">Data e horário</span>
                      <input
                        type="datetime-local"
                        value={sched.oneTime}
                        onChange={(e) => setSched((s) => ({ ...s, oneTime: e.target.value }))}
                        className="h-8 rounded-md border border-apolo-border bg-apolo-surface3 px-2 text-xs text-apolo-textPrimary focus:border-apolo-accent focus:outline-none"
                      />
                    </div>
                  )}

                  {/* Destino */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] uppercase tracking-wider text-apolo-textTertiary">Destino do resultado</span>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { value: 'apolo_inbox',   Icon: Inbox,        label: 'Apolo inbox',    hint: 'Notificação no app' },
                        { value: 'vault_obsidian', Icon: NotebookPen,  label: 'Vault Obsidian', hint: 'Nota automática'    },
                      ].map(({ value, Icon, label, hint }) => {
                        const active = sched.outputDest === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setSched((s) => ({ ...s, outputDest: value }))}
                            aria-pressed={active}
                            className={`flex items-start gap-2 rounded-lg border p-2 text-left transition ${
                              active
                                ? 'border-apolo-accent bg-apolo-accent/10'
                                : 'border-apolo-borderSubtle bg-apolo-surface3 hover:border-apolo-borderStrong'
                            }`}
                          >
                            <Icon size={12} aria-hidden="true" className={active ? 'text-apolo-accent' : 'text-apolo-textTertiary'} />
                            <div>
                              <p className={`text-[11px] font-medium ${active ? 'text-apolo-textPrimary' : 'text-apolo-textSecondary'}`}>{label}</p>
                              <p className="text-[9px] text-apolo-textTertiary">{hint}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Preview */}
                  <p className="inline-flex items-center gap-1.5 text-[10px] text-apolo-textTertiary">
                    <Clock size={9} aria-hidden="true" />
                    {sched.mode === 'once'
                      ? `Uma vez em ${(() => { try { return new Date(sched.oneTime).toLocaleString('pt-BR', { dateStyle:'short', timeStyle:'short' }); } catch { return '—'; } })()}`
                      : `${FREQ_OPTIONS.find(f => f.value === sched.frequency)?.label ?? '—'}${sched.frequency !== 'every_6h' ? ` às ${sched.time}` : ''}`
                    }
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {error && (
          <div role="alert" className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-xs text-apolo-error">
            {error}
          </div>
        )}

        <div className="mt-auto flex flex-col-reverse items-stretch gap-2 pt-2 lg:mt-1 lg:flex-row lg:items-center lg:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-11 rounded-md px-3 text-sm text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary lg:h-auto lg:py-1.5"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex h-11 items-center justify-center gap-1.5 rounded-md bg-apolo-accent px-3 text-sm font-medium text-apolo-bg transition hover:bg-apolo-accent/90 disabled:opacity-60 lg:h-auto lg:py-1.5"
          >
            {submitting && <Loader2 size={12} className="animate-spin" aria-hidden="true" />}
            {agent?.id ? 'Salvar' : 'Criar agente'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

// OllamaQuickStart — empty state quando Ollama tá online mas sem modelos
// pulados. Mostra 3 cards de modelos recomendados low-RAM com botão que
// copia o comando `ollama pull <nome>` pra clipboard + indicação de RAM
// requerida. Pedro escolhe qual cabe na máquina dele e cola no terminal.
function OllamaQuickStart({ onPullStarted, onRefresh }) {
  const [copiedModel, setCopiedModel] = useState(null);

  async function copyPullCommand(name) {
    const cmd = `ollama pull ${name}`;
    try {
      await navigator.clipboard.writeText(cmd);
      setCopiedModel(name);
      onPullStarted?.(name);
      setTimeout(() => setCopiedModel(null), 2_500);
    } catch {
      // clipboard falhou — não bloqueia, só não dá feedback visual
    }
  }

  return (
    <div className="mt-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface3/40 p-2.5">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-[10px] font-medium uppercase tracking-wider text-apolo-textTertiary">
          Comece com um modelo leve
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-1 text-[10px] text-apolo-textTertiary hover:text-apolo-textPrimary"
          title="Verificar de novo após pullar"
        >
          <RefreshCw size={9} aria-hidden="true" />
          recheck
        </button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {OLLAMA_QUICK_START.map((m) => {
          const copied = copiedModel === m.name;
          return (
            <li key={m.name}>
              <button
                type="button"
                onClick={() => copyPullCommand(m.name)}
                className="group flex w-full items-center justify-between gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 py-1.5 text-left transition hover:border-apolo-accent hover:bg-apolo-surface3"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  <div className="flex items-center gap-1.5">
                    <code className="font-mono text-[11px] font-medium text-apolo-textPrimary">
                      {m.name}
                    </code>
                    <span className="rounded-full bg-apolo-accent/15 px-1.5 py-0 text-[9px] font-medium text-apolo-accent">
                      {m.badge}
                    </span>
                  </div>
                  <span className="text-[10px] leading-tight text-apolo-textTertiary">
                    {m.desc} · ~{m.sizeGb}GB · {m.ram} RAM
                  </span>
                </div>
                <span
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition ${
                    copied
                      ? 'bg-apolo-success/20 text-apolo-success'
                      : 'text-apolo-textTertiary group-hover:bg-apolo-surface1 group-hover:text-apolo-accent'
                  }`}
                  aria-hidden="true"
                  title={copied ? 'Copiado!' : 'Copiar comando'}
                >
                  {copied ? <Check size={11} /> : <Copy size={11} />}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[10px] leading-relaxed text-apolo-textTertiary">
        <Download size={9} aria-hidden="true" className="mb-0.5 inline" /> Click no card pra copiar o comando.
        Cola no terminal, espera o download, depois clica em <strong>recheck</strong>.
      </p>
    </div>
  );
}

// OllamaModelPicker — combobox visual estilo ollama.com:
// trigger button mostra modelo selecionado, click abre dropdown com search
// + lista dos modelos locais reais (nome + tamanho legível + parâmetros).
// Pedro pediu paridade visual com a screenshot do app oficial do Ollama.
function OllamaModelPicker({ models, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => m.name.toLowerCase().includes(q));
  }, [models, query]);

  function selectModel(name) {
    onChange(name);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1.5 text-xs font-mono text-apolo-textPrimary transition hover:border-apolo-borderStrong focus:border-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
      >
        <span className="truncate">{value || 'Selecionar modelo…'}</span>
        <span aria-hidden="true" className="text-apolo-textTertiary">▾</span>
      </button>
      {open && (
        <div
          className="absolute left-0 right-0 z-50 mt-1 max-h-72 overflow-hidden rounded-md border border-apolo-borderSubtle bg-apolo-surface1 shadow-2xl"
          role="listbox"
        >
          <div className="border-b border-apolo-borderSubtle p-2">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find model…"
              autoFocus
              className="w-full rounded-md border border-apolo-border bg-apolo-surface2 px-2 py-1 text-xs focus:border-apolo-accent focus:outline-none"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <li className="px-3 py-4 text-center text-[11px] text-apolo-textTertiary">
                Nenhum modelo encontrado
              </li>
            )}
            {filtered.map((m) => {
              const active = value === m.name;
              const sizeGb = m.size ? (m.size / 1_000_000_000).toFixed(1) : null;
              return (
                <li key={m.name}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => selectModel(m.name)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs transition ${
                      active
                        ? 'bg-apolo-accent/15 text-apolo-textPrimary'
                        : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
                    }`}
                  >
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="truncate font-mono">{m.name}</span>
                      {(m.parameter_size || m.quantization) && (
                        <span className="truncate text-[10px] text-apolo-textTertiary">
                          {[m.parameter_size, m.quantization].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                    {sizeGb && (
                      <span className="shrink-0 text-[10px] text-apolo-textTertiary">
                        {sizeGb} GB
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
